import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useCurrency } from "@/hooks/use-currency";
import { Loader2, Download, DollarSign, TrendingUp, TrendingDown, Calendar, PieChart } from "lucide-react";
import { format } from "date-fns";

interface FinancialSummary {
    totalRevenue: number;
    totalBookings: number;
    totalDiscounts: number;
    totalCommissions: number;
    commissionsPaid: number;
    commissionsPending: number;
    netProfit: number;
    averageBookingValue: number;
}

interface MonthlyData {
    month: string;
    revenue: number;
    bookings: number;
    discounts: number;
    commissions: number;
    profit: number;
}

export function FinancialOverview() {
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState<FinancialSummary>({
        totalRevenue: 0,
        totalBookings: 0,
        totalDiscounts: 0,
        totalCommissions: 0,
        commissionsPaid: 0,
        commissionsPending: 0,
        netProfit: 0,
        averageBookingValue: 0
    });
    const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([]);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const { toast } = useToast();
    const { formatPrice } = useCurrency();

    useEffect(() => {
        fetchFinancialData();
    }, [startDate, endDate]);

    const fetchFinancialData = async () => {
        setLoading(true);
        try {
            // Build date filter
            let bookingsQuery = supabase
                .from('bookings')
                .select('*')
                .eq('booking_status', 'completed'); // Only count completed bookings for revenue

            if (startDate) {
                bookingsQuery = bookingsQuery.gte('created_at', new Date(startDate).toISOString());
            }
            if (endDate) {
                const endDateTime = new Date(endDate);
                endDateTime.setHours(23, 59, 59, 999);
                bookingsQuery = bookingsQuery.lte('created_at', endDateTime.toISOString());
            }

            const { data: bookings } = await bookingsQuery;

            // Fetch promo code redemptions
            const { data: promoRedemptions } = await supabase
                .from('promo_code_redemptions')
                .select('discount_amount, booking_id');

            // Fetch referral code redemptions
            const { data: referralRedemptions } = await supabase
                .from('referral_code_redemptions')
                .select('discount_amount, commission_amount, booking_id');

            // Fetch commission payments
            const { data: commissionPayments } = await supabase
                .from('commission_payments')
                .select('amount, payment_status');

            // Calculate totals
            const totalRevenue = (bookings || []).reduce((sum, b) => sum + (b.total_amount || 0), 0);
            const totalBookings = (bookings || []).length;

            const completedBookingIds = new Set((bookings || []).map(b => b.id));

            // Calculate discounts from both promo and referral codes (only for completed bookings)
            const promoDiscounts = (promoRedemptions || [])
                .filter(r => completedBookingIds.has(r.booking_id))
                .reduce((sum, r) => sum + (r.discount_amount || 0), 0);

            const referralDiscounts = (referralRedemptions || [])
                .filter(r => completedBookingIds.has(r.booking_id))
                .reduce((sum, r) => sum + (r.discount_amount || 0), 0);

            const totalDiscounts = promoDiscounts + referralDiscounts;

            // Calculate commissions (only for completed bookings)
            const totalCommissions = (referralRedemptions || [])
                .filter(r => completedBookingIds.has(r.booking_id))
                .reduce((sum, r) => sum + (r.commission_amount || 0), 0);

            const commissionsPaid = (commissionPayments || [])
                .filter(p => p.payment_status === 'PAID')
                .reduce((sum, p) => sum + (p.amount || 0), 0);
            const commissionsPending = totalCommissions - commissionsPaid;

            // Calculate net profit (revenue - commissions)
            const netProfit = totalRevenue - totalCommissions;

            setSummary({
                totalRevenue,
                totalBookings,
                totalDiscounts,
                totalCommissions,
                commissionsPaid,
                commissionsPending,
                netProfit,
                averageBookingValue: totalBookings > 0 ? totalRevenue / totalBookings : 0
            });

            // Calculate monthly breakdown
            const monthlyMap = new Map<string, MonthlyData>();

            (bookings || []).forEach(booking => {
                const month = format(new Date(booking.created_at), 'MMM yyyy');

                if (!monthlyMap.has(month)) {
                    monthlyMap.set(month, {
                        month,
                        revenue: 0,
                        bookings: 0,
                        discounts: 0,
                        commissions: 0,
                        profit: 0
                    });
                }

                const data = monthlyMap.get(month)!;
                data.revenue += booking.total_amount || 0;
                data.bookings += 1;

                // Add discounts for this booking
                const promoDiscount = (promoRedemptions || [])
                    .filter(r => r.booking_id === booking.id)
                    .reduce((sum, r) => sum + (r.discount_amount || 0), 0);
                const referralDiscount = (referralRedemptions || [])
                    .filter(r => r.booking_id === booking.id)
                    .reduce((sum, r) => sum + (r.discount_amount || 0), 0);
                data.discounts += promoDiscount + referralDiscount;

                // Add commissions for this booking
                const commission = (referralRedemptions || [])
                    .filter(r => r.booking_id === booking.id)
                    .reduce((sum, r) => sum + (r.commission_amount || 0), 0);
                data.commissions += commission;

                data.profit = data.revenue - data.commissions;
            });

            setMonthlyData(Array.from(monthlyMap.values()).reverse());

        } catch (error) {
            console.error('Error fetching financial data:', error);
            toast({
                title: "Error",
                description: "Failed to fetch financial data",
                variant: "destructive"
            });
        } finally {
            setLoading(false);
        }
    };

    const exportToCSV = () => {
        const summaryRows = [
            ['Financial Summary', ''],
            ['Total Revenue', summary.totalRevenue],
            ['Total Bookings', summary.totalBookings],
            ['Average Booking Value', summary.averageBookingValue],
            ['Total Discounts Given', summary.totalDiscounts],
            ['Total Commissions Earned', summary.totalCommissions],
            ['Commissions Paid', summary.commissionsPaid],
            ['Commissions Pending', summary.commissionsPending],
            ['Net Profit', summary.netProfit],
            [''],
            ['Monthly Breakdown', ''],
            ['Month', 'Revenue', 'Bookings', 'Discounts', 'Commissions', 'Net Profit']
        ];

        const monthlyRows = monthlyData.map(m => [
            m.month,
            m.revenue,
            m.bookings,
            m.discounts,
            m.commissions,
            m.profit
        ]);

        const csvContent = [...summaryRows, ...monthlyRows]
            .map(row => row.join(','))
            .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `financial-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);

        toast({ title: "Report Exported", description: "Financial report downloaded successfully" });
    };

    const profitMargin = summary.totalRevenue > 0
        ? ((summary.netProfit / summary.totalRevenue) * 100).toFixed(1)
        : '0';

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-3xl font-bold tracking-tight">Financial Overview</h2>
                    <p className="text-muted-foreground">Complete business financial summary and analytics.</p>
                </div>
                <Button onClick={exportToCSV}>
                    <Download className="mr-2 h-4 w-4" />
                    Export Report
                </Button>
            </div>

            {/* Date Filters */}
            <Card>
                <CardHeader>
                    <CardTitle>Date Range Filter</CardTitle>
                    <CardDescription>Filter financial data by date range</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid gap-4 md:grid-cols-3">
                        <div className="space-y-2">
                            <Label>Start Date</Label>
                            <Input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>End Date</Label>
                            <Input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>&nbsp;</Label>
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setStartDate("");
                                    setEndDate("");
                                }}
                                className="w-full"
                            >
                                Clear Filters
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {loading ? (
                <div className="flex justify-center p-8">
                    <Loader2 className="animate-spin h-8 w-8 text-primary" />
                </div>
            ) : (
                <>
                    {/* Summary Cards */}
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                                <DollarSign className="h-4 w-4 text-muted-foreground" />
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold text-green-600">{formatPrice(summary.totalRevenue)}</div>
                                <p className="text-xs text-muted-foreground">
                                    {summary.totalBookings} bookings
                                </p>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-medium">Net Profit</CardTitle>
                                <TrendingUp className="h-4 w-4 text-muted-foreground" />
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold text-blue-600">{formatPrice(summary.netProfit)}</div>
                                <p className="text-xs text-muted-foreground">
                                    {profitMargin}% profit margin
                                </p>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-medium">Total Commissions</CardTitle>
                                <TrendingDown className="h-4 w-4 text-muted-foreground" />
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold text-orange-600">{formatPrice(summary.totalCommissions)}</div>
                                <p className="text-xs text-muted-foreground">
                                    {formatPrice(summary.commissionsPending)} pending
                                </p>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-medium">Avg Booking Value</CardTitle>
                                <PieChart className="h-4 w-4 text-muted-foreground" />
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold">{formatPrice(summary.averageBookingValue)}</div>
                                <p className="text-xs text-muted-foreground">
                                    Per booking
                                </p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Detailed Breakdown */}
                    <Tabs defaultValue="summary" className="w-full">
                        <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger value="summary">Summary</TabsTrigger>
                            <TabsTrigger value="monthly">Monthly Breakdown</TabsTrigger>
                        </TabsList>

                        <TabsContent value="summary" className="space-y-4">
                            <Card>
                                <CardHeader>
                                    <CardTitle>Financial Summary</CardTitle>
                                    <CardDescription>Complete breakdown of all financial metrics</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                                            <div>
                                                <p className="text-sm text-muted-foreground">Total Revenue</p>
                                                <p className="text-2xl font-bold text-green-600">{formatPrice(summary.totalRevenue)}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm text-muted-foreground">Total Bookings</p>
                                                <p className="text-2xl font-bold">{summary.totalBookings}</p>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4 p-4 bg-red-50 rounded-lg border border-red-200">
                                            <div>
                                                <p className="text-sm text-red-700">Total Discounts</p>
                                                <p className="text-xl font-bold text-red-600">-{formatPrice(summary.totalDiscounts)}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm text-orange-700">Total Commissions</p>
                                                <p className="text-xl font-bold text-orange-600">-{formatPrice(summary.totalCommissions)}</p>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4 p-4 bg-green-50 rounded-lg border border-green-200">
                                            <div>
                                                <p className="text-sm text-green-700">Commissions Paid</p>
                                                <p className="text-xl font-bold text-green-600">{formatPrice(summary.commissionsPaid)}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm text-orange-700">Commissions Pending</p>
                                                <p className="text-xl font-bold text-orange-600">{formatPrice(summary.commissionsPending)}</p>
                                            </div>
                                        </div>

                                        <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-400">
                                            <p className="text-sm text-blue-700 mb-1">Net Profit (Revenue - Commissions)</p>
                                            <p className="text-3xl font-bold text-blue-600">{formatPrice(summary.netProfit)}</p>
                                            <p className="text-sm text-blue-600 mt-1">{profitMargin}% profit margin</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </TabsContent>

                        <TabsContent value="monthly" className="space-y-4">
                            <Card>
                                <CardHeader>
                                    <CardTitle>Monthly Breakdown</CardTitle>
                                    <CardDescription>Revenue, commissions, and profit by month</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    {monthlyData.length === 0 ? (
                                        <div className="text-center py-8 text-muted-foreground">
                                            No data available for the selected period
                                        </div>
                                    ) : (
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Month</TableHead>
                                                    <TableHead className="text-right">Bookings</TableHead>
                                                    <TableHead className="text-right">Revenue</TableHead>
                                                    <TableHead className="text-right">Discounts</TableHead>
                                                    <TableHead className="text-right">Commissions</TableHead>
                                                    <TableHead className="text-right">Net Profit</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {monthlyData.map((data) => (
                                                    <TableRow key={data.month}>
                                                        <TableCell className="font-medium">{data.month}</TableCell>
                                                        <TableCell className="text-right">{data.bookings}</TableCell>
                                                        <TableCell className="text-right text-green-600 font-semibold">
                                                            {formatPrice(data.revenue)}
                                                        </TableCell>
                                                        <TableCell className="text-right text-red-600">
                                                            -{formatPrice(data.discounts)}
                                                        </TableCell>
                                                        <TableCell className="text-right text-orange-600">
                                                            -{formatPrice(data.commissions)}
                                                        </TableCell>
                                                        <TableCell className="text-right text-blue-600 font-bold">
                                                            {formatPrice(data.profit)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                <TableRow className="bg-muted/50 font-bold">
                                                    <TableCell>Total</TableCell>
                                                    <TableCell className="text-right">{summary.totalBookings}</TableCell>
                                                    <TableCell className="text-right text-green-600">
                                                        {formatPrice(summary.totalRevenue)}
                                                    </TableCell>
                                                    <TableCell className="text-right text-red-600">
                                                        -{formatPrice(summary.totalDiscounts)}
                                                    </TableCell>
                                                    <TableCell className="text-right text-orange-600">
                                                        -{formatPrice(summary.totalCommissions)}
                                                    </TableCell>
                                                    <TableCell className="text-right text-blue-600">
                                                        {formatPrice(summary.netProfit)}
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    )}
                                </CardContent>
                            </Card>
                        </TabsContent>
                    </Tabs>
                </>
            )}
        </div>
    );
}
